import attentionMapService from '@/domain/services/generalPanel/attentionMap';

export const MAP_PAGE_SIZE = 50;

/**
 * Quantas páginas o ciclo pede ao mesmo tempo.
 *
 * O endpoint faz um número fixo de consultas por página, então uma rajada de
 * páginas simultâneas multiplica consultas no banco pelo tamanho da frota: 200
 * páginas viram 600 consultas concorrentes num só ciclo. Pior, o ciclo é atômico
 * de propósito — quanto mais chamadas voam juntas, maior a chance de uma falhar e
 * derrubar a atualização inteira, e aí a tela envelhece sem nunca dar erro
 * visível. Seis mantém a carga previsível e ainda esconde a latência da rede.
 */
export const MAX_PARALLEL_PAGES = 6;

/**
 * Pede as páginas em lotes, esperando cada lote antes de abrir o próximo.
 *
 * @param {Function} requestPage Pede uma página pelo número.
 * @param {Number} totalPages Total de páginas anunciado pela primeira resposta.
 * @returns {Promise<Object[]>} As respostas da segunda página em diante, em ordem.
 */
async function loadRemainingPages(requestPage, totalPages) {
  const pending = [];
  for (let page = 2; page <= totalPages; page += 1) {
    pending.push(page);
  }

  const responses = [];
  while (pending.length) {
    const batch = pending.splice(0, MAX_PARALLEL_PAGES);
    // O laço existe para segurar o ritmo, e não para processar: o lote seguinte
    // só sai quando o atual termina.
    // eslint-disable-next-line no-await-in-loop
    const settled = await Promise.all(batch.map(requestPage));

    responses.push(...settled);
  }

  return responses;
}

/**
 * Carrega todas as páginas do Mapa de Atenção como uma operação única.
 *
 * O ciclo é atômico, como o do Painel de Status de Comunicação: se qualquer
 * página falhar, a promessa é rejeitada e a tela mantém o último conjunto
 * completo, deixando o relógio de idade envelhecer. O mapa nunca desenha meia
 * frota como se fosse inteira.
 *
 * @param {Object} [filter] Escopo da tela.
 * @returns {Promise<Object[]>} Todas as instalações do escopo.
 */
export default async function loadAllInstallations(filter) {
  const requestPage = (page) => attentionMapService.getList({
    filter,
    page,
    pageSize: MAP_PAGE_SIZE,
  });

  const firstPage = await requestPage(1);
  const totalPages = firstPage.totalPages || 1;

  if (totalPages <= 1) {
    return firstPage.data || [];
  }

  const responses = await loadRemainingPages(requestPage, totalPages);

  return (firstPage.data || []).concat(
    ...responses.map((response) => response.data || []),
  );
}
